import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Lock, Mail } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import logoAzul from "@/assets/logo-azul.webp";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const { signIn, signUp, user, hasAdminAccess, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || "/admin";

  useEffect(() => {
    // Redireciona após login bem-sucedido
    // O ProtectedRoute vai verificar se tem acesso e mostrar "Acesso Negado" se necessário
    if (!loading && user) {
      navigate(from, { replace: true });
    }
  }, [user, loading, navigate, from]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim() || !password.trim()) {
      toast({
        title: "Campos obrigatórios",
        description: "Preencha email e senha.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      if (isSignUp) {
        const { error } = await signUp(email.trim(), password);

        if (error) {
          let message = "Erro ao criar conta. Tente novamente.";
          
          if (error.message.includes("User already registered")) {
            message = "Este email já está cadastrado. Faça login.";
          } else if (error.message.includes("Password should be")) {
            message = "A senha deve ter pelo menos 6 caracteres.";
          }

          toast({
            title: "Erro no cadastro",
            description: message,
            variant: "destructive",
          });
          return;
        }

        toast({
          title: "Conta criada!",
          description: "Aguarde a liberação de acesso pelo administrador.",
        });
        setIsSignUp(false);
        setPassword("");
      } else {
        const { error } = await signIn(email.trim(), password);

        if (error) {
          let message = "Erro ao fazer login. Tente novamente.";
          
          if (error.message.includes("Invalid login credentials")) {
            message = "Email ou senha incorretos.";
          } else if (error.message.includes("Email not confirmed")) {
            message = "Email não confirmado. Verifique sua caixa de entrada.";
          }

          toast({
            title: "Erro no login",
            description: message,
            variant: "destructive",
          });
          return;
        }

        toast({
          title: "Login realizado!",
          description: "Bem-vindo ao painel administrativo.",
        });
      }
    } catch (err) {
      toast({
        title: "Erro",
        description: "Ocorreu um erro inesperado.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Login Admin | Aceena</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen flex items-center justify-center bg-muted p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-4 text-center">
            <div className="flex justify-center">
              <img src={logoAzul} alt="Aceena" className="h-10" />
            </div>
            <div>
              <CardTitle className="text-2xl font-bold text-foreground">
                {isSignUp ? "Criar Conta" : "Painel Administrativo"}
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                {isSignUp 
                  ? "Crie sua conta para solicitar acesso" 
                  : "Faça login para gerenciar o blog"}
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-foreground">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    disabled={isLoading}
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-foreground">Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    disabled={isLoading}
                    autoComplete="current-password"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isSignUp ? "Criando conta..." : "Entrando..."}
                  </>
                ) : (
                  isSignUp ? "Criar Conta" : "Entrar"
                )}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => {
                  setIsSignUp(!isSignUp);
                  setPassword("");
                }}
                className="text-sm text-primary hover:underline"
              >
                {isSignUp 
                  ? "Já tem conta? Faça login" 
                  : "Não tem conta? Criar conta"}
              </button>
            </div>

            <div className="mt-6 text-center">
              <a
                href="/"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                ← Voltar para o site
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
